import { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { analystApi, streamQuery } from 'api';
import { AuthContext } from 'components/context/AuthContext';
import BeboMascot from './BeboMascot';
import './BeboIA.css';

const hashHue = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
};

const TOOL_LABELS = {
  list_brands: 'las marcas',
  get_brand_metrics: 'las métricas',
  compare_brands: 'la comparación de marcas',
  compare_periods: 'la evolución del período',
  account_review: 'la review de la cuenta',
  get_campaigns: 'campañas y anuncios',
  get_stock: 'el stock',
  prepare_reallocation: 'la reasignación de presupuesto',
  reconcile_supplier_files: 'los archivos de proveedores vs Fenicio',
};

// Update the last message in the list (the streaming bot message).
const patchLast = (messages, patch) => {
  if (!messages.length) return messages;
  const last = messages[messages.length - 1];
  const changes = typeof patch === 'function' ? patch(last) : patch;
  return [...messages.slice(0, -1), { ...last, ...changes }];
};

const suggestionsFor = (brand) => {
  if (brand?.vertical === 'lead_gen') {
    return [
      'Haceme un resumen de los leads de ayer',
      '¿Cuál es el CPL y cómo viene vs el objetivo del mes?',
      '¿Qué campañas están trayendo los leads más baratos?',
    ];
  }
  return [
    '¿Cuáles son los 5 productos más vendidos del mes?',
    'Haceme un resumen de las ventas del día de ayer',
    '¿Qué productos están con riesgo de quebrar stock?',
  ];
};

function BrandLogo({ name }) {
  const label = name || '?';
  return (
    <span className="bebo-logo" style={{ background: `hsl(${hashHue(label)} 52% 46%)` }}>
      {label.slice(0, 1).toUpperCase()}
    </span>
  );
}

function BeboAvatar() {
  return (
    <span className="bebo-avatar" aria-hidden="true">
      <svg viewBox="0 0 174 175" fill="none">
        <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#2f3df7" />
        <path d="M19.63 39.26C30.4713 39.26 39.26 30.4713 39.26 19.63C39.26 8.78865 30.4713 0 19.63 0C8.78865 0 0 8.78865 0 19.63C0 30.4713 8.78865 39.26 19.63 39.26Z" fill="#2f3df7" />
        <path d="M86.12 39.26C96.9613 39.26 105.75 30.4713 105.75 19.63C105.75 8.78865 96.9613 0 86.12 0C75.2787 0 66.42 8.78865 66.49 19.63C66.49 30.4713 75.2787 39.26 86.12 39.26Z" fill="#2f3df7" />
      </svg>
    </span>
  );
}

const money = (v) =>
  v == null ? null : `$${Number(v).toLocaleString('es-UY', { maximumFractionDigits: 0 })}`;

const MAX_SWING = 0.5; // must match backend DEFAULT_MAX_BUDGET_SWING_PCT

const CTA_OPTIONS = [
  'LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'SUBSCRIBE', 'GET_OFFER', 'ORDER_NOW',
  'BOOK_TRAVEL', 'CONTACT_US', 'SEND_MESSAGE', 'DOWNLOAD', 'APPLY_NOW', 'SEE_MENU',
];

function ActionCard({ proposal, brand }) {
  const isPause = proposal.action === 'PAUSE';
  const isDuplicate = proposal.action === 'DUPLICATE_AD';
  const isCreate = proposal.action === 'CREATE_AD_FROM_ASSET';
  const isMulti = proposal.action === 'CREATE_MULTISIZE_AD';
  const isPublish = isDuplicate || isCreate || isMulti; // publisher actions: no budget editor, apply as-is
  const dest = proposal.dest || {};
  const asset = proposal.asset || {};
  const assets = proposal.assets || [];
  const before = proposal.before?.budget_value ?? null;
  const [state, setState] = useState('idle'); // idle | applying | applied | failed | dismissed
  const [err, setErr] = useState(null);
  const [after, setAfter] = useState(
    proposal.after?.budget_value != null ? String(Math.round(proposal.after.budget_value)) : ''
  );

  if (proposal.status === 'error') {
    return <div className="bebo-ac warn">No pude preparar la acción: {proposal.message}</div>;
  }
  if (proposal.status === 'not_found') {
    return (
      <div className="bebo-ac warn">
        No encontré «{proposal.query}» en esta marca. Probá con el nombre exacto de la campaña o anuncio.
      </div>
    );
  }
  if (proposal.status === 'ambiguous') {
    return (
      <div className="bebo-ac warn">
        Hay varias que coinciden: {(proposal.candidates || []).join(' · ')}. ¿Cuál querés?
      </div>
    );
  }

  const entity = proposal.entity || {};
  const kindWord = entity.type === 'campaign' ? 'campaña' : 'anuncio';
  const kicker = isPause
    ? `Pausar ${kindWord}`
    : isDuplicate
      ? 'Duplicar anuncio'
      : `Ajustar ppto · ${kindWord}`;

  const afterNum = Number(after);
  const validNum = Number.isFinite(afterNum) && afterNum > 0;
  const pct = !isPause && before > 0 && validNum ? Math.round(((afterNum - before) / before) * 100) : null;
  const swingOk = before > 0 && validNum && Math.abs(afterNum - before) / before <= MAX_SWING;
  const canApply = isPause || isPublish ? true : swingOk;

  const apply = async () => {
    setState('applying');
    const applyPayload = isPause || isPublish
      ? proposal.apply_payload
      : { ...proposal.apply_payload, budget: { mode: 'ABSOLUTE', value: afterNum } };
    try {
      const res = await analystApi.applyAction({
        brand,
        provider: proposal.provider,
        action: proposal.action,
        entity,
        apply_payload: applyPayload,
      });
      if (res?.ok) setState('applied');
      else {
        setErr(res?.error || 'No se pudo aplicar.');
        setState('failed');
      }
    } catch (e) {
      setErr('No se pudo aplicar. Reintentá en un momento.');
      setState('failed');
    }
  };

  return (
    <div className={`bebo-ac2 ${!isPause && !isPublish && !swingOk ? 'warnedge' : ''}`}>
      <div className="bebo-ac2-info">
        {isPause ? (
          <b>{entity.name || '—'}</b>
        ) : isDuplicate ? (
          <>
            <span className="k">{kicker} ·</span> <b>{entity.name || '—'}</b>
            <span className="chg">
              <span className="arr">→</span>
              <span className="to">{dest.name || '—'}</span>
              <span className="pausebadge">en pausa</span>
            </span>
          </>
        ) : isCreate ? (
          <>
            <span className="k">Crear anuncio ·</span>
            {asset.url && <img className="bebo-ac2-thumb" src={asset.url} alt={asset.filename || ''} />}
            <b>{asset.filename || 'creativo'}</b>
            <span className="chg">
              <span className="arr">→</span>
              <span className="to">{dest.name || '—'}</span>
              <span className="pausebadge">en pausa</span>
            </span>
          </>
        ) : isMulti ? (
          <>
            <span className="k">Crear anuncio · {assets.length} medidas ·</span>
            {assets.slice(0, 4).map((a) => (
              <img key={a.id} className="bebo-ac2-thumb" src={a.url} alt={a.filename || ''} title={a.aspect_ratio} />
            ))}
            <span className="chg">
              <span className="arr">→</span>
              <span className="to">{dest.name || '—'}</span>
              <span className="pausebadge">en pausa</span>
            </span>
          </>
        ) : (
          <>
            <span className="k">{kicker} ·</span> <b>{entity.name || '—'}</b>
            <span className="chg">
            <span className="from">{money(before) || '—'}</span>
            <span className="arr">→</span>
            <span className="edit">
              $
              <input
                type="number"
                value={after}
                min="1"
                onChange={(e) => setAfter(e.target.value)}
                aria-label="Nuevo presupuesto diario"
              />
              <small>/día</small>
            </span>
            {pct != null && (
              <span className={`pct ${!swingOk ? 'bad' : pct < 0 ? 'down' : 'up'}`}>
                {pct > 0 ? '+' : ''}
                {pct}%
              </span>
            )}
            </span>
          </>
        )}
      </div>

      <div className="bebo-ac2-act">
        {state === 'applied' ? (
          <span className="done">✓ Aplicado</span>
        ) : state === 'failed' ? (
          <span className="fail">✕ No se pudo</span>
        ) : state === 'dismissed' ? (
          <span className="muted">Descartada</span>
        ) : (
          <>
            <button
              type="button"
              className="apply"
              disabled={!canApply || state === 'applying'}
              onClick={apply}
            >
              {state === 'applying'
                ? '…'
                : isPause
                  ? `Pausar ${kindWord}`
                  : isPublish
                    ? 'Publicar en pausa'
                    : 'Aplicar'}
            </button>
            <button
              type="button"
              className="x"
              aria-label="Descartar"
              onClick={() => setState('dismissed')}
            >
              ✕
            </button>
          </>
        )}
      </div>

      {!isPause && !isPublish && !swingOk && validNum && (
        <div className="bebo-ac2-hint">El cambio supera el máximo permitido ({MAX_SWING * 100}%) en una sola acción.</div>
      )}
      {!validNum && !isPause && !isPublish && <div className="bebo-ac2-hint">Ingresá un presupuesto válido.</div>}
      {state === 'failed' && err && <div className="bebo-ac2-hint err">{err}</div>}
    </div>
  );
}

const platformLabel = (p) => (p === 'google' || p === 'google_ads' ? 'Google' : 'Meta');
const trendArrow = (t) => (t === 'up' ? '↑' : t === 'down' ? '↓' : '→');
const signedMoney = (v) =>
  v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`;

// account_review v2: one editable table to reallocate budget across campaigns,
// with a net total and a single Apply (best-effort, per-row outcome).
function ReallocationCard({ proposal, brand }) {
  const rows = proposal.rows || [];
  const [budgets, setBudgets] = useState(() =>
    rows.map((r) => (r.is_pause ? '0' : String(Math.round(r.budget_after ?? r.budget_before ?? 0))))
  );
  const [state, setState] = useState('idle'); // idle | applying | done
  const [results, setResults] = useState(null);

  const perRow = rows.map((r, i) => {
    const before = r.budget_before ?? 0;
    // Budget couldn't be read (Meta ad-set-level / Click-to-WhatsApp / error): the
    // row is informational only — never editable, never applied, out of the net.
    const unreadable = !r.is_pause && (before <= 0 || r.status === 'error' || r.status === 'not_found');
    const val = r.is_pause ? 0 : Number(budgets[i]);
    const validNum = r.is_pause || (Number.isFinite(val) && val > 0);
    const delta = unreadable ? null : validNum ? val - before : null;
    const swingOk =
      r.is_pause || unreadable || (before > 0 ? Math.abs(val - before) / before <= MAX_SWING : validNum);
    return { before, unreadable, val, validNum, delta, swingOk };
  });
  const net = perRow.reduce((s, p) => s + (p.unreadable ? 0 : p.delta || 0), 0);
  const actionable = perRow.filter((p) => !p.unreadable);
  const allValid = actionable.length > 0 && actionable.every((p) => p.validNum && p.swingOk);
  const excluded = perRow.filter((p) => p.unreadable).length;

  const apply = async () => {
    setState('applying');
    const applyRows = rows
      .map((r, i) => ({ r, i }))
      .filter(({ i }) => !perRow[i].unreadable)
      .map(({ r, i }) =>
        r.is_pause
          ? { ...r }
          : { ...r, apply_payload: { ...r.apply_payload, budget: { mode: 'ABSOLUTE', value: Number(budgets[i]) } } }
      );
    try {
      const res = await analystApi.applyReallocation({ brand, rows: applyRows });
      setResults(res?.rows || []);
    } catch (e) {
      setResults(rows.map((r) => ({ campaign: r.campaign, ok: false })));
    }
    setState('done');
  };

  const okCount = results ? results.filter((o) => o.ok).length : 0;

  return (
    <div className="bebo-realloc">
      <div className="bebo-realloc-h">Reasignación de presupuesto{brand ? ` · ${brand}` : ''}</div>
      <div className="bebo-realloc-tbl">
        <div className="rr rr-head">
          <span>Campaña</span>
          <span>ROAS/CPL</span>
          <span>Tend.</span>
          <span>Actual</span>
          <span>Nuevo/día</span>
          <span>Δ</span>
        </div>
        {rows.map((r, i) => {
          const p = perRow[i];
          const outcome = results && results.find((o) => o.campaign === r.campaign);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div className={`rr ${p.unreadable ? 'muted' : !p.swingOk ? 'bad' : ''}`} key={i}>
              <span className="nm" title={r.adset ? `${r.campaign} › ${r.adset}` : r.campaign}>
                <b>{r.adset || r.campaign}</b>
                <small>
                  {platformLabel(r.platform || r.provider)}
                  {r.adset ? ` · ${r.campaign}` : ''}
                </small>
              </span>
              <span>{r.roas != null ? `${r.roas.toFixed(1)}x` : r.cpl != null ? money(r.cpl) : '—'}</span>
              <span className={`trend ${r.trend || ''}`}>{trendArrow(r.trend)}</span>
              <span>{money(p.before) || '—'}</span>
              <span className="new">
                {r.is_pause ? (
                  <em>Pausar</em>
                ) : p.unreadable ? (
                  <span className="sd" title={r.reason || 'No se pudo leer el presupuesto actual'}>s/d</span>
                ) : state !== 'idle' ? (
                  money(p.val)
                ) : (
                  <>
                    $
                    <input
                      type="number"
                      min="1"
                      value={budgets[i]}
                      onChange={(e) =>
                        setBudgets((b) => b.map((x, j) => (j === i ? e.target.value : x)))
                      }
                      aria-label={`Nuevo presupuesto de ${r.campaign}`}
                    />
                  </>
                )}
              </span>
              <span className={`d ${p.delta > 0 ? 'up' : p.delta < 0 ? 'down' : ''}`}>
                {signedMoney(p.delta)}
                {outcome && <span className={`oc ${outcome.ok ? 'ok' : 'fail'}`}>{outcome.ok ? '✓' : '✕'}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="bebo-realloc-f">
        {Math.abs(net) >= 1 ? (
          <span className="net-alert">
            {net > 0 ? 'Escalando' : 'Desescalando'} la cuenta · {signedMoney(net)}/día
          </span>
        ) : (
          <span className="net-alert muted">Reasignación neutra · neto ~$0</span>
        )}
        {state === 'done' ? (
          <span className="done">✓ Aplicada · {okCount}/{results.length}</span>
        ) : (
          <button type="button" className="apply" disabled={!allValid || state === 'applying'} onClick={apply}>
            {state === 'applying' ? 'Aplicando…' : 'Aplicar reasignación'}
          </button>
        )}
      </div>
      {state === 'idle' && !allValid && (
        <div className="bebo-ac2-hint">
          Cada cambio debe ser &gt; 0 y no superar {MAX_SWING * 100}% en un paso (0 = pausar lo decide Bebo).
        </div>
      )}
      {excluded > 0 && (
        <div className="bebo-ac2-hint">
          {excluded === 1 ? '1 campaña queda' : `${excluded} campañas quedan`} fuera (s/d): no se pudo leer su
          presupuesto — típico de campañas Meta con budget a nivel ad-set o Click-to-WhatsApp.
        </div>
      )}
    </div>
  );
}

function Composer({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  files,
  creatives,
  onAttach,
  onRemoveFile,
  onRemoveCreative,
  onBuild,
  onClone,
  uploading,
}) {
  const fileRef = useRef(null);
  return (
    <div className="bebo-composer">
      <div className="bebo-glow" />
      {creatives?.length > 0 && (
        <div className="bebo-files bebo-creatives">
          {creatives.map((c) => (
            <span className="bebo-creativechip" key={c.asset_id} title={c.filename}>
              {c.url && <img src={c.url} alt={c.filename} />}
              <span className="bebo-filename">{c.filename}</span>
              {c.aspect_ratio && <small>{c.aspect_ratio}</small>}
              <button
                type="button"
                onClick={() => onRemoveCreative?.(c.asset_id)}
                aria-label={`Quitar ${c.filename}`}
              >
                ×
              </button>
            </span>
          ))}
          {onBuild && (
            <button type="button" className="bebo-buildbtn" onClick={onBuild}>
              Armar anuncio →
            </button>
          )}
        </div>
      )}
      {files?.length > 0 && (
        <div className="bebo-files">
          {files.map((f) => (
            <span className="bebo-filechip" key={f.file_id} title={f.filename}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path
                  d="M14 3v5h5M8 13h8M8 17h8M9 9h1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 3h9l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="bebo-filename">{f.filename}</span>
              <small>{f.row_count} filas</small>
              <button
                type="button"
                onClick={() => onRemoveFile?.(f.file_id)}
                aria-label={`Quitar ${f.filename}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <form className="bebo-pill" onSubmit={onSubmit}>
        {onAttach && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.xlsx,image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={(e) => {
                Array.from(e.target.files || []).forEach((f) => onAttach(f));
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="bebo-attach"
              disabled={disabled || uploading}
              onClick={() => fileRef.current?.click()}
              aria-label="Adjuntar CSV, Excel o imagen"
              title="Adjuntar CSV, Excel o imagen"
            >
              {uploading ? (
                <span className="bebo-attach-spin" />
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path
                    d="M21 12.5 12.5 21a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10.5 19a2 2 0 0 1-3-3L15 8.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </>
        )}
        {onClone && (
          <button
            type="button"
            className="bebo-attach"
            onClick={onClone}
            aria-label="Clonar un ad set"
            title="Clonar un ad set"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="11" height="11" rx="2" strokeLinejoin="round" />
              <path d="M5 15V5a2 2 0 0 1 2-2h8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <input
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
          aria-label="Preguntá a Bebo"
        />
        <button className="bebo-send" type="submit" disabled={disabled} aria-label="Enviar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </form>
    </div>
  );
}

const CHART_COLORS = [
  '#6C7BFF', '#22C7A9', '#E8853B', '#B57BFF', '#5AA9FF', '#F2C14E', '#EF6F6C', '#7AD1A0',
];
const CHART_TOOLTIP = {
  background: '#1b1e2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
  fontSize: 12, color: '#e6e8f5',
};
const CHART_AXIS_TICK = { fill: '#8a8fb0', fontSize: 11 };

const fmtNum = (v) => {
  if (typeof v !== 'number') return v;
  const digits = Math.abs(v) >= 1000 || Number.isInteger(v) ? 0 : 2;
  return v.toLocaleString('es-UY', { maximumFractionDigits: digits });
};

function ChartCard({ spec }) {
  if (!spec || !Array.isArray(spec.data) || spec.data.length === 0) return null;
  const { type, title, x_key: xKey, series = [], data, stacked, unit } = spec;
  const isPie = type === 'pie' || type === 'donut';
  const hasRight = series.some((s) => s.axis === 'right');

  return (
    <div className="bebo-chart">
      {title && <div className="bebo-chart-title">{title}</div>}
      <ResponsiveContainer width="100%" height={260}>
        {isPie ? (
          <PieChart>
            <Pie
              data={data}
              dataKey={series[0].key}
              nameKey={xKey}
              innerRadius={type === 'donut' ? 55 : 0}
              outerRadius={92}
              paddingAngle={2}
            >
              {data.map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={fmtNum} contentStyle={CHART_TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        ) : (
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey={xKey} tick={CHART_AXIS_TICK} tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
            <YAxis yAxisId="left" tick={CHART_AXIS_TICK} tickLine={false} axisLine={false}
              tickFormatter={fmtNum} width={48} />
            {hasRight && (
              <YAxis yAxisId="right" orientation="right" tick={CHART_AXIS_TICK} tickLine={false}
                axisLine={false} tickFormatter={fmtNum} width={48} />
            )}
            <Tooltip formatter={fmtNum} contentStyle={CHART_TOOLTIP}
              labelStyle={{ color: '#cfd3ea' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {series.map((s, i) => {
              const color = CHART_COLORS[i % CHART_COLORS.length];
              const axisId = s.axis === 'right' ? 'right' : 'left';
              const stackId = stacked ? 'a' : undefined;
              if (s.type === 'bar') {
                return (
                  <Bar key={s.key} yAxisId={axisId} dataKey={s.key} name={s.label}
                    fill={color} radius={[4, 4, 0, 0]} stackId={stackId} />
                );
              }
              if (s.type === 'area') {
                return (
                  <Area key={s.key} yAxisId={axisId} dataKey={s.key} name={s.label}
                    stroke={color} fill={color} fillOpacity={0.18} strokeWidth={2}
                    stackId={stackId} />
                );
              }
              return (
                <Line key={s.key} yAxisId={axisId} dataKey={s.key} name={s.label}
                  stroke={color} strokeWidth={2} dot={false} />
              );
            })}
          </ComposedChart>
        )}
      </ResponsiveContainer>
      {unit && <div className="bebo-chart-unit">en {unit}</div>}
    </div>
  );
}

function reconcileChangeText(ch) {
  if (ch.field === 'stock') return ch.new ? `Reponer → ${ch.new}` : 'Sin stock';
  if (ch.field === 'price') return `Precio nuevo: ${ch.new}`;
  return `${ch.field}: ${ch.new}`;
}

const RECONCILE_PREVIEW = 8;

function ReconcilePreviewTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bebo-rec-block">
      <div className="bebo-rec-subtitle">
        {title} <span>({rows.length})</span>
      </div>
      <table className="bebo-rec-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Producto</th>
            <th>Cambio</th>
            <th>Origen</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, RECONCILE_PREVIEW).map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`${r.sku}-${i}`}>
              <td>{r.sku}</td>
              <td>{r.product_name || '—'}</td>
              <td>
                {r.changes && r.changes.length
                  ? r.changes.map(reconcileChangeText).join(' · ')
                  : '(revisar)'}
              </td>
              <td className="bebo-rec-src">{r.source || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > RECONCILE_PREVIEW && (
        <div className="bebo-rec-more">
          y {rows.length - RECONCILE_PREVIEW} más — en el Excel
        </div>
      )}
    </div>
  );
}

function ReconcileCard({ result }) {
  const [downloading, setDownloading] = useState(false);
  if (!result || !result.summary) return null;
  const s = result.summary;

  const download = async () => {
    setDownloading(true);
    try {
      await analystApi.exportReconcile(result);
    } catch (e) {
      toast.error(e?.message || 'No se pudo generar el Excel.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bebo-rec">
      <div className="bebo-rec-head">
        <span className="bebo-rec-title">
          Reconciliación vs Fenicio
          {result.brand ? ` · ${result.brand}` : ''}
        </span>
        <button type="button" className="bebo-rec-dl" onClick={download} disabled={downloading}>
          {downloading ? 'Generando…' : 'Descargar Excel'}
        </button>
      </div>
      <div className="bebo-rec-summary">
        {s.sin_stock > 0 && (
          <span className="bebo-rec-urgent">⚠ {s.sin_stock} sin stock</span>
        )}
        {s.cambios} cambios · {s.a_revisar} a revisar · {s.sin_match} sin match
        {result.snapshot_date ? ` · snapshot ${result.snapshot_date}` : ''}
      </div>
      {(result.by_source || []).length > 1 && (
        <div className="bebo-rec-bysrc">
          {result.by_source.map((f) => (
            <div key={f.source} className="bebo-rec-bysrc-row">
              <span className="bebo-rec-bysrc-name">{f.source}</span>
              <span>
                {f.cambios} cambios · {Math.round((f.match_rate || 0) * 100)}% match
                {f.match_rate < 0.2 ? ' · ¿otra marca?' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      <ReconcilePreviewTable title="Cambios" rows={result.cambios} />
      <ReconcilePreviewTable title="A revisar" rows={result.a_revisar} />
      {s.sin_match > 0 && (
        <div className="bebo-rec-more">{s.sin_match} ítems sin match — detalle en el Excel</div>
      )}
    </div>
  );
}

// Picker-driven ad builder: choose destination campaign -> ad set and a template
// ad from real lists (no name guessing), then prepare the create-from-asset card.
// Inherited-copy editor for one ad in the builder. `fields` is the editable copy
// (name/message/headline/description/link/cta); `onChange(key, value)` lifts edits.
function AdCopyEditor({ fields, onChange }) {
  return (
    <div className="bebo-builder-edit">
      <div className="bebo-builder-edit-h">Editá antes de publicar (heredado de la plantilla)</div>
      <label className="bebo-builder-f">
        <span>Nombre del anuncio</span>
        <input value={fields.name || ''} onChange={(e) => onChange('name', e.target.value)} />
      </label>
      <label className="bebo-builder-f">
        <span>Texto principal</span>
        <textarea rows={3} value={fields.message || ''} onChange={(e) => onChange('message', e.target.value)} />
      </label>
      <label className="bebo-builder-f">
        <span>Título</span>
        <input value={fields.headline || ''} onChange={(e) => onChange('headline', e.target.value)} />
      </label>
      <label className="bebo-builder-f">
        <span>Descripción</span>
        <input value={fields.description || ''} onChange={(e) => onChange('description', e.target.value)} />
      </label>
      <label className="bebo-builder-f">
        <span>URL de destino</span>
        <input value={fields.link || ''} onChange={(e) => onChange('link', e.target.value)} />
      </label>
      <label className="bebo-builder-f">
        <span>Botón (CTA)</span>
        <select value={fields.cta || 'LEARN_MORE'} onChange={(e) => onChange('cta', e.target.value)}>
          {CTA_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function prepErrorText(p) {
  const base = p?.summary || p?.error?.que_paso || p?.message || 'No se pudo preparar el anuncio.';
  const detail = p?.error?.technical?.message; // Meta's raw message (names the invalid field)
  return detail ? `${base} — Meta: ${detail}` : base;
}

// Meta wants start/end as ISO8601 with a UTC offset ("2026-10-12T15:30:00-0300");
// <input type="datetime-local"> gives local wall time ("2026-10-12T15:30"). These
// convert both ways using the browser's own offset.
const _pad = (n) => String(n).padStart(2, '0');
function _nowLocalInput() {
  const d = new Date();
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
}
function metaIsoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
}
function localInputToMetaIso(local) {
  if (!local) return '';
  const d = new Date(local); // parsed as local wall time
  if (Number.isNaN(d.getTime())) return '';
  const off = -d.getTimezoneOffset(); // minutes east of UTC
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}:00${sign}${_pad(Math.floor(abs / 60))}${_pad(abs % 60)}`;
}

// The "mesa": compose N ads into one ad set at once. Each uploaded piece is
// assigned to an ad number — pieces sharing a number become ONE ad (several
// sizes, paso 0); different numbers become separate ads (paso 1). All share the
// chosen ad set + template (page / URL / CTA base) and publish PAUSED.
function CreateAdPanel({ brand, creatives, onClose }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  // asset_id -> ad number (1-based); 0 = "sin usar". Default: all in ad 1.
  const [slots, setSlots] = useState(() => {
    const m = {};
    creatives.forEach((c) => {
      m[c.asset_id] = 1;
    });
    return m;
  });
  const [campId, setCampId] = useState('');
  const [adsetId, setAdsetId] = useState('');
  const [defaultTemplateId, setDefaultTemplateId] = useState(''); // plantilla para todos
  const [templates, setTemplates] = useState({}); // slot -> templateId (override del default)
  const [ads, setAds] = useState(null); // prepared ads: [{slot, pieces, proposal, fields, pub}]
  const [activeAd, setActiveAd] = useState(0); // which ad card is shown in the deck
  const [preparing, setPreparing] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    analystApi
      .getMetaStructure(brand)
      .then((d) => {
        if (alive) setTree(d?.campaigns || []);
      })
      .catch(() => {
        if (alive) setErr('No pude cargar las campañas de la marca.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [brand]);

  const camp = tree.find((c) => c.id === campId);
  const adsets = camp?.adsets || [];
  const adset = adsets.find((a) => a.id === adsetId);
  const templateAds = useMemo(
    () =>
      tree
        .flatMap((c) =>
          (c.adsets || []).flatMap((a) => (a.ads || []).map((ad) => ({ ...ad, campaign: c.name })))
        )
        .sort((x, y) => (y.spend || 0) - (x.spend || 0)),
    [tree]
  );
  // Group pieces by ad number, in ascending order. Each group is one ad.
  const groups = useMemo(() => {
    const used = [...new Set(creatives.map((c) => slots[c.asset_id]).filter((n) => n > 0))].sort(
      (a, b) => a - b
    );
    return used.map((n) => ({
      slot: n,
      pieces: creatives.filter((c) => slots[c.asset_id] === n),
    }));
  }, [creatives, slots]);
  const maxSlot = groups.length ? Math.max(...groups.map((g) => g.slot)) : 0;
  const slotOptions = Array.from({ length: maxSlot + 1 }, (_, i) => i + 1); // + one new ad
  const multi = creatives.length > 1;

  // Each ad uses its own template, or the default when it has none.
  const templateFor = (slot) => templates[slot] || defaultTemplateId;
  const canPrepare =
    groups.length >= 1 && adsetId && groups.every((g) => templateFor(g.slot)) && !preparing;

  const setSlot = (assetId, n) => setSlots((s) => ({ ...s, [assetId]: n }));
  const setTemplate = (slot, id) => setTemplates((t) => ({ ...t, [slot]: id }));

  // Any destination / grouping change invalidates prepared ads (must re-prepare).
  useEffect(() => {
    setAds(null);
  }, [campId, adsetId, defaultTemplateId, templates, slots]);

  const prepare = async () => {
    setPreparing(true);
    setErr(null);
    setAds(null);
    try {
      const prepared = [];
      for (const g of groups) {
        const tid = templateFor(g.slot);
        const tname = templateAds.find((t) => t.id === tid)?.name;
        // eslint-disable-next-line no-await-in-loop
        const p = await analystApi.prepareCreativeAd({
          brand,
          asset_ids: g.pieces.map((c) => c.asset_id),
          template_ad_id: tid,
          template_name: tname,
          dest_adset_id: adsetId,
          dest_adset_name: adset?.name,
        });
        prepared.push({
          slot: g.slot,
          pieces: g.pieces,
          proposal: p,
          fields: p?.status === 'ready' ? p.editable || {} : null,
          pub: { state: 'idle', err: null },
        });
      }
      setAds(prepared);
      setActiveAd(0);
    } catch (_e) {
      setErr('No se pudieron preparar los anuncios.');
    } finally {
      setPreparing(false);
    }
  };

  const setField = (idx, key, value) =>
    setAds((list) => list.map((a, i) => (i === idx ? { ...a, fields: { ...a.fields, [key]: value } } : a)));
  const setPub = (idx, pub) =>
    setAds((list) => list.map((a, i) => (i === idx ? { ...a, pub: { ...a.pub, ...pub } } : a)));

  const publishOne = async (idx, list) => {
    const a = (list || ads)[idx];
    if (!a || a.proposal?.status !== 'ready') return;
    if (a.pub.state === 'applying' || a.pub.state === 'applied') return;
    setPub(idx, { state: 'applying', err: null });
    try {
      const res = await analystApi.applyAction({
        brand,
        provider: a.proposal.provider,
        action: a.proposal.action,
        entity: a.proposal.entity,
        apply_payload: { ...a.proposal.apply_payload, ...(a.fields || {}) },
      });
      if (res?.ok) setPub(idx, { state: 'applied' });
      else setPub(idx, { state: 'failed', err: res?.error || 'No se pudo publicar.' });
    } catch (_e) {
      setPub(idx, { state: 'failed', err: 'No se pudo publicar. Reintentá en un momento.' });
    }
  };

  const publishAll = async () => {
    const snapshot = ads;
    for (let i = 0; i < snapshot.length; i++) {
      const s = snapshot[i].pub.state;
      if (snapshot[i].proposal?.status === 'ready' && (s === 'idle' || s === 'failed')) {
        // eslint-disable-next-line no-await-in-loop
        await publishOne(i, snapshot);
      }
    }
  };

  const readyCount = (ads || []).filter((a) => a.proposal?.status === 'ready').length;
  const appliedCount = (ads || []).filter((a) => a.pub.state === 'applied').length;
  const pendingCount = (ads || []).filter(
    (a) => a.proposal?.status === 'ready' && a.pub.state !== 'applied'
  ).length;
  const publishing = (ads || []).some((a) => a.pub.state === 'applying');

  return (
    <div className="bebo-builder-overlay" onClick={onClose} role="presentation">
      <div className="bebo-builder" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="bebo-builder-h">
          <b>Armar anuncios · {brand}</b>
          <button type="button" className="x" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="bebo-builder-pieces">
          {multi && (
            <div className="bebo-builder-edit-h">
              Agrupá las piezas: mismo nº = 1 anuncio con varias medidas · nº distinto = anuncios
              separados en el mismo ad set.
            </div>
          )}
          {creatives.map((c) => (
            <div key={c.asset_id} className="bebo-builder-piece">
              {c.url && <img src={c.url} alt={c.filename} />}
              <span className="nm">{c.filename}</span>
              {c.aspect_ratio && <span className="rt">{c.aspect_ratio}</span>}
              {multi && (
                <select
                  className="bebo-builder-slot"
                  value={slots[c.asset_id] ?? 0}
                  onChange={(e) => setSlot(c.asset_id, Number(e.target.value))}
                  aria-label={`Anuncio para ${c.filename}`}
                >
                  {slotOptions.map((n) => (
                    <option key={n} value={n}>
                      Anuncio {n}
                    </option>
                  ))}
                  <option value={0}>Sin usar</option>
                </select>
              )}
            </div>
          ))}
          {multi && groups.length > 0 && (
            <div className="bebo-builder-grouphint">
              {groups.length === 1
                ? '→ 1 anuncio'
                : `→ ${groups.length} anuncios`}
              {groups.some((g) => g.pieces.length > 1) &&
                ` (${groups
                  .map((g) => (g.pieces.length > 1 ? `nº${g.slot}: ${g.pieces.length} medidas` : null))
                  .filter(Boolean)
                  .join(' · ')})`}
            </div>
          )}
        </div>

        {loading ? (
          <div className="bebo-builder-empty">Cargando campañas…</div>
        ) : tree.length === 0 ? (
          <div className="bebo-builder-empty">No encontré campañas Meta para esta marca.</div>
        ) : (
          <>
            <label className="bebo-builder-f">
              <span>Campaña (destino)</span>
              <select
                value={campId}
                onChange={(e) => {
                  setCampId(e.target.value);
                  setAdsetId('');
                }}
              >
                <option value="">Elegí…</option>
                {tree.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="bebo-builder-f">
              <span>Ad set (destino)</span>
              <select value={adsetId} onChange={(e) => setAdsetId(e.target.value)} disabled={!campId}>
                <option value="">Elegí…</option>
                {adsets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="bebo-builder-f">
              <span>
                Plantilla {groups.length > 1 ? 'por defecto ' : ''}(base: página / URL / CTA)
              </span>
              <select value={defaultTemplateId} onChange={(e) => setDefaultTemplateId(e.target.value)}>
                <option value="">Elegí…</option>
                {templateAds.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.campaign}
                  </option>
                ))}
              </select>
            </label>

            {groups.length > 1 && (
              <div className="bebo-builder-pertmpl">
                <div className="bebo-builder-edit-h">Plantilla por anuncio (opcional, pisa la por defecto)</div>
                {groups.map((g) => (
                  <label key={g.slot} className="bebo-builder-f">
                    <span>
                      Anuncio {g.slot} ·{' '}
                      {g.pieces.length > 1 ? `${g.pieces.length} medidas` : g.pieces[0]?.filename}
                    </span>
                    <select value={templateFor(g.slot)} onChange={(e) => setTemplate(g.slot, e.target.value)}>
                      <option value="">Usar la por defecto</option>
                      {templateAds.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {t.campaign}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}

            {!ads && (
              <button type="button" className="bebo-builder-go" disabled={!canPrepare} onClick={prepare}>
                {preparing
                  ? 'Preparando…'
                  : `Preparar ${groups.length} ${groups.length === 1 ? 'anuncio' : 'anuncios'}`}
              </button>
            )}
          </>
        )}

        {err && <div className="bebo-builder-err">{err}</div>}

        {ads && (() => {
          const idx = Math.min(activeAd, ads.length - 1);
          const a = ads[idx];
          const tabState = (x) =>
            x.pub.state === 'applied' ? 'done'
              : x.proposal?.status !== 'ready' ? 'bad'
                : x.pub.state === 'failed' ? 'fail' : 'ready';
          return (
            <div className="bebo-deck">
              <div className="bebo-deck-tabs">
                {ads.map((x, i) => (
                  <button
                    key={x.slot}
                    type="button"
                    className={`bebo-deck-tab ${i === idx ? 'active' : ''} ${tabState(x)}`}
                    onClick={() => setActiveAd(i)}
                  >
                    <span className="dot" />
                    Anuncio {x.slot}
                  </button>
                ))}
              </div>

              <div className="bebo-deck-stage">
                {ads.length > 1 && (
                  <button
                    type="button"
                    className="bebo-deck-nav"
                    disabled={idx === 0}
                    onClick={() => setActiveAd(Math.max(0, idx - 1))}
                    aria-label="Anterior"
                  >
                    ◀
                  </button>
                )}

                <div className={`bebo-builder-card bebo-deck-card ${ads.length > 1 ? 'stacked' : ''}`}>
                  <div className="bebo-builder-adhead">
                    <span className="n">Anuncio {a.slot}</span>
                    {a.pieces.length > 1 && <span className="rt">{a.pieces.length} medidas</span>}
                    {a.pieces.slice(0, 4).map((p) => (
                      <img key={p.asset_id} className="bebo-ac2-thumb" src={p.url} alt={p.filename} title={p.aspect_ratio} />
                    ))}
                    <span className="to">→ {adset?.name || '—'}</span>
                    <span className="pausebadge">en pausa</span>
                    {a.proposal?.status === 'ready' ? (
                      <span className="ok">● listo</span>
                    ) : (
                      <span className="bad">● no va</span>
                    )}
                  </div>

                  {a.proposal?.status === 'ready' ? (
                    <>
                      {a.fields && <AdCopyEditor fields={a.fields} onChange={(k, v) => setField(idx, k, v)} />}
                      <div className="bebo-ac2-act">
                        {a.pub.state === 'applied' ? (
                          <span className="done">✓ Publicado</span>
                        ) : (
                          <button
                            type="button"
                            className="apply"
                            disabled={a.pub.state === 'applying'}
                            onClick={() => publishOne(idx)}
                          >
                            {a.pub.state === 'applying' ? '…' : a.pub.state === 'failed' ? 'Reintentar' : 'Publicar en pausa'}
                          </button>
                        )}
                      </div>
                      {a.pub.state === 'failed' && a.pub.err && (
                        <div className="bebo-ac2-hint err">{a.pub.err}</div>
                      )}
                    </>
                  ) : (
                    <div className="bebo-ac2-hint err">{prepErrorText(a.proposal)}</div>
                  )}
                </div>

                {ads.length > 1 && (
                  <button
                    type="button"
                    className="bebo-deck-nav"
                    disabled={idx === ads.length - 1}
                    onClick={() => setActiveAd(Math.min(ads.length - 1, idx + 1))}
                    aria-label="Siguiente"
                  >
                    ▶
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {ads && readyCount > 1 && (
          <div className="bebo-builder-footer">
            {appliedCount > 0 && (
              <span className="muted">
                {appliedCount}/{readyCount} publicados
              </span>
            )}
            {pendingCount > 0 && (
              <button type="button" className="bebo-builder-go" disabled={publishing} onClick={publishAll}>
                {publishing ? 'Publicando…' : `Publicar ${pendingCount} en pausa`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Paso 2: clone an existing ad set (deep copy + its ads), edit the ad-set settings
// (name / budget / schedule) and each cloned ad's copy, then publish PAUSED.
function CloneAdsetPanel({ brand, onClose }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campId, setCampId] = useState('');
  const [adsetId, setAdsetId] = useState('');
  const [proposal, setProposal] = useState(null);
  const [adsetFields, setAdsetFields] = useState(null);
  const [ads, setAds] = useState([]); // [{ad_id, name, copy, editable, changed}]
  const [activeAd, setActiveAd] = useState(0);
  const [preparing, setPreparing] = useState(false);
  const [pub, setPub] = useState({ state: 'idle', err: null, result: null });
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    analystApi
      .getMetaStructure(brand)
      .then((d) => alive && setTree(d?.campaigns || []))
      .catch(() => alive && setErr('No pude cargar las campañas de la marca.'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [brand]);

  const camp = tree.find((c) => c.id === campId);
  const adsets = camp?.adsets || [];
  const adset = adsets.find((a) => a.id === adsetId);
  const canPrepare = adsetId && !preparing;

  useEffect(() => {
    setProposal(null);
    setAdsetFields(null);
    setAds([]);
    setPub({ state: 'idle', err: null, result: null });
  }, [campId, adsetId]);

  const prepare = async () => {
    setPreparing(true);
    setErr(null);
    try {
      const p = await analystApi.prepareAdsetClone({
        brand,
        source_adset_id: adsetId,
        source_adset_name: adset?.name,
      });
      if (p?.status === 'ready') {
        setProposal(p);
        // Default start to NOW (the source start is usually in the past → Meta
        // rejects it); keep the source end only if it's set. Both as local-input.
        setAdsetFields({
          ...p.editable.adset,
          start_time: _nowLocalInput(),
          end_time: metaIsoToLocalInput(p.editable.adset.end_time),
        });
        setAds((p.editable.ads || []).map((a) => ({ ...a, changed: false })));
        setActiveAd(0);
      } else {
        setErr(prepErrorText(p));
      }
    } catch (_e) {
      setErr('No se pudo preparar la clonación.');
    } finally {
      setPreparing(false);
    }
  };

  const setAdsetField = (k, v) => setAdsetFields((f) => ({ ...f, [k]: v }));
  const setAdCopy = (idx, k, v) =>
    setAds((list) => list.map((a, i) => (i === idx ? { ...a, copy: { ...a.copy, [k]: v }, changed: true } : a)));
  const setAdName = (idx, v) =>
    setAds((list) => list.map((a, i) => (i === idx ? { ...a, name: v, changed: true } : a)));

  const publish = async () => {
    setPub({ state: 'applying', err: null, result: null });
    const overrides = { name: adsetFields.name };
    if (!adsetFields.budget_at_campaign && adsetFields.daily_budget)
      overrides.daily_budget = Number(adsetFields.daily_budget);
    if (adsetFields.start_time) overrides.start_time = localInputToMetaIso(adsetFields.start_time);
    if (adsetFields.end_time) overrides.end_time = localInputToMetaIso(adsetFields.end_time);
    try {
      const res = await analystApi.applyAction({
        brand,
        provider: proposal.provider,
        action: proposal.action,
        entity: proposal.entity,
        apply_payload: {
          ...proposal.apply_payload,
          adset: overrides,
          ads: ads.map((a) => ({
            ad_id: a.ad_id, // source ad — each is copied individually (Meta sync limit)
            name: a.name,
            copy: a.copy,
            editable: a.editable,
            changed: a.changed,
          })),
        },
      });
      if (res?.ok) {
        setPub({ state: 'applied', err: null, result: res.result });
      } else {
        const detail = res?.detail?.technical?.message; // Meta's raw message (names the field)
        const msg = res?.error || 'No se pudo clonar.';
        setPub({ state: 'failed', err: detail ? `${msg} — Meta: ${detail}` : msg, result: null });
      }
    } catch (e) {
      // A non-2xx from the server still carries a useful body — surface it.
      const detail = e?.response?.data?.error || e?.response?.data?.message || e?.message;
      setPub({
        state: 'failed',
        err: detail ? `No se pudo clonar — ${detail}` : 'No se pudo clonar. Reintentá en un momento.',
        result: null,
      });
    }
  };

  const idx = Math.min(activeAd, Math.max(0, ads.length - 1));
  const activeAdObj = ads[idx];

  return (
    <div className="bebo-builder-overlay" onClick={onClose} role="presentation">
      <div className="bebo-builder" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="bebo-builder-h">
          <b>Clonar ad set · {brand}</b>
          <button type="button" className="x" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {loading ? (
          <div className="bebo-builder-empty">Cargando campañas…</div>
        ) : tree.length === 0 ? (
          <div className="bebo-builder-empty">No encontré campañas Meta para esta marca.</div>
        ) : (
          <>
            <label className="bebo-builder-f">
              <span>Campaña</span>
              <select
                value={campId}
                onChange={(e) => {
                  setCampId(e.target.value);
                  setAdsetId('');
                }}
              >
                <option value="">Elegí…</option>
                {tree.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="bebo-builder-f">
              <span>Ad set a clonar</span>
              <select value={adsetId} onChange={(e) => setAdsetId(e.target.value)} disabled={!campId}>
                <option value="">Elegí…</option>
                {adsets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>

            {!proposal && (
              <button type="button" className="bebo-builder-go" disabled={!canPrepare} onClick={prepare}>
                {preparing ? 'Preparando…' : 'Preparar clonación'}
              </button>
            )}
          </>
        )}

        {err && <div className="bebo-builder-err">{err}</div>}

        {proposal && adsetFields && (
          <>
            <div className="bebo-builder-card">
              <div className="bebo-builder-edit-h">Ad set nuevo (se clona en pausa, misma campaña)</div>
              <label className="bebo-builder-f">
                <span>Nombre del ad set</span>
                <input value={adsetFields.name || ''} onChange={(e) => setAdsetField('name', e.target.value)} />
              </label>
              <label className="bebo-builder-f">
                <span>Presupuesto diario{adsetFields.budget_at_campaign ? ' (está en la campaña — no editable acá)' : ''}</span>
                <input
                  type="number"
                  min="1"
                  value={adsetFields.daily_budget ?? ''}
                  disabled={adsetFields.budget_at_campaign}
                  onChange={(e) => setAdsetField('daily_budget', e.target.value)}
                />
              </label>
              <label className="bebo-builder-f">
                <span>Inicio</span>
                <input
                  type="datetime-local"
                  value={adsetFields.start_time || ''}
                  min={_nowLocalInput()}
                  onChange={(e) => setAdsetField('start_time', e.target.value)}
                />
              </label>
              <label className="bebo-builder-f">
                <span>Fin (opcional)</span>
                <input
                  type="datetime-local"
                  value={adsetFields.end_time || ''}
                  min={adsetFields.start_time || _nowLocalInput()}
                  onChange={(e) => setAdsetField('end_time', e.target.value)}
                />
              </label>
            </div>

            {ads.length > 0 && activeAdObj && (
              <div className="bebo-deck">
                <div className="bebo-deck-tabs">
                  {ads.map((x, i) => (
                    <button
                      key={x.ad_id}
                      type="button"
                      className={`bebo-deck-tab ${i === idx ? 'active' : ''} ${x.changed ? 'ready' : ''}`}
                      onClick={() => setActiveAd(i)}
                    >
                      <span className="dot" />
                      Anuncio {i + 1}
                    </button>
                  ))}
                </div>
                <div className="bebo-deck-stage">
                  {ads.length > 1 && (
                    <button type="button" className="bebo-deck-nav" disabled={idx === 0}
                      onClick={() => setActiveAd(Math.max(0, idx - 1))} aria-label="Anterior">◀</button>
                  )}
                  <div className={`bebo-builder-card bebo-deck-card ${ads.length > 1 ? 'stacked' : ''}`}>
                    <div className="bebo-builder-adhead">
                      <span className="n">Anuncio {idx + 1}</span>
                      <span className="pausebadge">en pausa</span>
                      {!activeAdObj.editable && <span className="bad">● copy no editable</span>}
                    </div>
                    {activeAdObj.editable ? (
                      <AdCopyEditor
                        fields={{ name: activeAdObj.name, ...activeAdObj.copy }}
                        onChange={(k, v) => (k === 'name' ? setAdName(idx, v) : setAdCopy(idx, k, v))}
                      />
                    ) : (
                      <div className="bebo-ac2-hint">
                        Este anuncio se clona tal cual (no es imagen/enlace editable).
                      </div>
                    )}
                  </div>
                  {ads.length > 1 && (
                    <button type="button" className="bebo-deck-nav" disabled={idx === ads.length - 1}
                      onClick={() => setActiveAd(Math.min(ads.length - 1, idx + 1))} aria-label="Siguiente">▶</button>
                  )}
                </div>
              </div>
            )}

            <div className="bebo-builder-footer">
              {pub.state === 'applied' ? (
                <span className="done">✓ Ad set clonado en pausa</span>
              ) : (
                <button type="button" className="bebo-builder-go" disabled={pub.state === 'applying'} onClick={publish}>
                  {pub.state === 'applying' ? 'Clonando…' : 'Clonar en pausa'}
                </button>
              )}
            </div>
            {pub.state === 'failed' && pub.err && <div className="bebo-ac2-hint err">{pub.err}</div>}
            {pub.state === 'applied' && pub.result?.warnings?.length > 0 && (
              <div className="bebo-ac2-hint">Avisos: {pub.result.warnings.join(' · ')}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BeboIA() {
  const { auth } = useContext(AuthContext);
  const firstName = auth?.user?.first_name || auth?.user?.name || '';

  const [brands, setBrands] = useState([]);
  const [current, setCurrent] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]); // CSV/Excel attached to the conversation
  const [creatives, setCreatives] = useState([]); // images sent to the current brand's library
  const [uploading, setUploading] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const scrollRef = useRef(null);
  // Word-by-word reveal: deltas land in pendingRef; a timer drips them into the
  // visible message so text appears steadily (like Claude Code), not in bursts.
  const pendingRef = useRef('');
  const revealRef = useRef(null);
  const doneRef = useRef(false);

  const revealTick = useCallback(() => {
    const buf = pendingRef.current;
    if (!buf) {
      if (doneRef.current && revealRef.current) {
        clearInterval(revealRef.current);
        revealRef.current = null;
        setMessages((m) => patchLast(m, { streaming: false, status: null }));
      }
      return;
    }
    // Reveal to the next word boundary; drain faster when a backlog builds up.
    let end = buf.indexOf(' ', 1);
    end = end === -1 ? buf.length : end + 1;
    const n = Math.max(end, Math.ceil(buf.length / 10));
    pendingRef.current = buf.slice(n);
    setMessages((m) => patchLast(m, (b) => ({ content: b.content + buf.slice(0, n), status: null })));
  }, []);

  useEffect(
    () => () => {
      if (revealRef.current) clearInterval(revealRef.current);
    },
    []
  );

  useEffect(() => {
    let alive = true;
    analystApi
      .getBrands()
      .then((data) => {
        if (!alive) return;
        const list = data?.brands || [];
        setBrands(list);
        setCurrent(list[0] || null);
      })
      .catch(() => toast.error('No se pudieron cargar las marcas.'));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const send = useCallback(
    async (q) => {
      const question = (q ?? input).trim();
      if (!question || loading) return;
      // Session memory: last few text turns (question + answer) for follow-ups.
      const history = messages
        .filter((m) => m.content && !m.error && !m.streaming)
        .map((m) => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.content }))
        .slice(-6);
      // Flush any still-revealing text from a previous turn, then reset the buffer.
      if (revealRef.current) {
        clearInterval(revealRef.current);
        revealRef.current = null;
      }
      const leftover = pendingRef.current;
      pendingRef.current = '';
      doneRef.current = false;
      setInput('');
      setMessages((m) => {
        const flushed = leftover
          ? patchLast(m, (b) => ({ content: b.content + leftover, streaming: false }))
          : m;
        return [
          ...flushed,
          { role: 'user', content: question },
          { role: 'bot', content: '', status: null, streaming: true },
        ];
      });
      setLoading(true);
      revealRef.current = setInterval(revealTick, 24);
      try {
        await streamQuery(question, current?.name, {
          history,
          fileIds: files.map((f) => f.file_id),
          creativeIds: creatives.map((c) => c.asset_id),
          onStatus: (tool) =>
            setMessages((m) => patchLast(m, { status: TOOL_LABELS[tool] || tool })),
          onDelta: (text) => {
            pendingRef.current += text;
          },
          onActionProposal: (proposal) =>
            setMessages((m) =>
              patchLast(m, (b) => ({ proposals: [...(b.proposals || []), proposal] }))
            ),
          onReallocationProposal: (proposal) =>
            setMessages((m) =>
              patchLast(m, (b) => ({ reallocations: [...(b.reallocations || []), proposal] }))
            ),
          onReconcileResult: (result) =>
            // Keep only the latest result — if Bebo re-runs with another mapping,
            // the newest reconciliation replaces the previous card (no duplicates).
            setMessages((m) => patchLast(m, () => ({ reconciles: [result] }))),
          onChart: (spec) =>
            setMessages((m) =>
              patchLast(m, (b) => ({ charts: [...(b.charts || []), spec] }))
            ),
          onError: (msg) => {
            pendingRef.current = '';
            setMessages((m) =>
              patchLast(m, { content: msg, error: true, streaming: false, status: null })
            );
          },
        });
        doneRef.current = true; // let the reveal timer drain, then clear streaming
      } catch (err) {
        pendingRef.current = '';
        doneRef.current = true;
        const msg = 'Bebo no pudo responder. Reintentá en un momento.';
        setMessages((m) =>
          patchLast(m, (b) => ({
            content: b.content || msg,
            error: !b.content,
            streaming: false,
            status: null,
          }))
        );
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [input, loading, current, messages, files, creatives, revealTick]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  const handleAttach = useCallback(
    async (file) => {
      const isImage = /^image\//.test(file.type || '');
      setUploading(true);
      try {
        if (isImage) {
          // Images go to the SELECTED brand's creative library — never the CSV parser.
          if (!current?.name) {
            toast.error('Elegí una marca antes de subir un creativo.');
            return;
          }
          const res = await analystApi.uploadCreatives(current.name, [file]);
          const r = (res?.results || [])[0] || {};
          if (!r.ok) {
            toast.error(r.error || 'No se pudo subir el creativo.');
            return;
          }
          setCreatives((c) => [...c, { ...r.asset, _dup: !!r.duplicate }]);
          toast.success(
            r.duplicate
              ? `Ese creativo ya estaba en la biblioteca de ${current.name}.`
              : `Creativo agregado a la biblioteca de ${current.name}.`
          );
        } else {
          const res = await analystApi.uploadFile(file);
          setFiles((f) => [...f, res]);
          toast.success(`Archivo adjuntado: ${res.filename} (${res.row_count} filas)`);
        }
      } catch (err) {
        // The backend returns the clear reason (bad format, too big, over the row limit).
        toast.error(err.message || 'No se pudo subir el archivo.');
      } finally {
        setUploading(false);
      }
    },
    [current]
  );

  const removeFile = useCallback(
    (id) => setFiles((f) => f.filter((x) => x.file_id !== id)),
    []
  );

  const removeCreative = useCallback(
    (id) => setCreatives((c) => c.filter((x) => x.asset_id !== id)),
    []
  );

  const hasChat = messages.length > 0;

  return (
    <div className={`bebo ${hasChat ? '' : 'bebo--empty'}`}>
      {builderOpen && current && creatives.length > 0 && (
        <CreateAdPanel
          brand={current.name}
          creatives={creatives}
          onClose={() => setBuilderOpen(false)}
        />
      )}
      {cloneOpen && current && (
        <CloneAdsetPanel brand={current.name} onClose={() => setCloneOpen(false)} />
      )}
      <div className="bebo-topbar">
        <div className="bebo-brandsel" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => setMenuOpen((o) => !o)} aria-haspopup="listbox">
            <BrandLogo name={current?.name} />
            <span>{current?.name || 'Elegí una marca'}</span>
            <svg className="bebo-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" strokeLinecap="round" />
            </svg>
          </button>
          {menuOpen && (
            <div className="bebo-menu" role="listbox">
              <div className="mh">Marca a consultar</div>
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={`bebo-brandrow ${b.id === current?.id ? 'sel' : ''}`}
                  onClick={() => {
                    setCurrent(b);
                    setMenuOpen(false);
                    setMessages([]);
                    setInput('');
                  }}
                  role="option"
                  aria-selected={b.id === current?.id}
                >
                  <BrandLogo name={b.name} />
                  <div className="meta">
                    <div className="nm">{b.name}</div>
                    <div className="cs">{(b.connectors || []).join(' · ') || 'sin conectores'}</div>
                  </div>
                  {b.id === current?.id && (
                    <span className="check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bebo-hint">
          <span className="bebo-live" /> Warehouse · sincronizado 2×/día
        </div>
      </div>

      <div className="bebo-scroll" ref={scrollRef}>
        {!hasChat ? (
          <section className="bebo-hero">
            <h1 className="bebo-greet">{firstName ? `Hola, ${firstName}` : 'Hola'}</h1>
            <p className="bebo-sub">
              {current ? (
                <>
                  Preguntá sobre <b>{current.name}</b>. Bebo consulta el warehouse, nunca
                  inventa números.
                </>
              ) : (
                'Cargando marcas…'
              )}
            </p>
            <div className="bebo-hero-composer">
              <Composer
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onSubmit={onSubmit}
                placeholder={current ? `Preguntá sobre ${current.name}…` : 'Preguntá a Bebo…'}
                disabled={loading || !current}
                files={files}
                creatives={creatives}
                onAttach={handleAttach}
                onRemoveFile={removeFile}
                onRemoveCreative={removeCreative}
                onBuild={() => setBuilderOpen(true)}
                onClone={() => setCloneOpen(true)}
                uploading={uploading}
              />
            </div>
            {current && (
              <div className="bebo-suggests">
                {suggestionsFor(current).map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </section>
        ) : (
          <div className="bebo-thread">
            {messages.map((m, i) =>
              m.role === 'user' ? (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="bebo-msg user">
                  <div className="bubble">{m.content}</div>
                </div>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="bebo-msg bot">
                  <div className="row">
                    <BeboAvatar />
                    <div className="bebo-botbody">
                      {m.error ? (
                        <div className="bebo-err">{m.content}</div>
                      ) : (
                        <>
                          {m.status && (
                            <div className="bebo-toolchip">
                              Consultando {m.status}
                              <span className="bebo-dots">
                                <span />
                                <span />
                                <span />
                              </span>
                            </div>
                          )}
                          {m.content ? (
                            <div className="bebo-answer">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {m.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            !m.status &&
                            m.streaming && (
                              <div className="bebo-toolchip">
                                Pensando
                                <span className="bebo-dots">
                                  <span />
                                  <span />
                                  <span />
                                </span>
                              </div>
                            )
                          )}
                          {(m.proposals || []).map((p, pi) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ActionCard key={pi} proposal={p} brand={current?.name} />
                          ))}
                          {(m.reallocations || []).map((p, pi) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ReallocationCard key={`rr${pi}`} proposal={p} brand={current?.name} />
                          ))}
                          {(m.charts || []).map((c, ci) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ChartCard key={`ch${ci}`} spec={c} />
                          ))}
                          {(m.reconciles || []).map((r, ri) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ReconcileCard key={`rec${ri}`} result={r} />
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="bebo-bottom">
        <Composer
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onSubmit={onSubmit}
          placeholder={
            current
              ? hasChat
                ? `Seguí preguntando sobre ${current.name}…`
                : `Preguntá sobre ${current.name}…`
              : 'Preguntá a Bebo…'
          }
          disabled={loading || !current}
          files={files}
          creatives={creatives}
          onAttach={handleAttach}
          onRemoveFile={removeFile}
          onRemoveCreative={removeCreative}
          onBuild={() => setBuilderOpen(true)}
          onClone={() => setCloneOpen(true)}
          uploading={uploading}
        />
      </div>

      {!auth?.user?.is_growth_partner && <BeboMascot brand={current?.name} onAsk={send} />}
    </div>
  );
}

export default BeboIA;
